import React, { useState, useEffect } from "react";
import { useUserContext } from "../config/UserContext";
import { useKeycloak } from "../config/KeycloakContext";

interface CustomFieldsCardProps {
  config: any[];
  onContinue: () => void;
  onBack?: () => void;
}

const CustomFieldsCard: React.FC<CustomFieldsCardProps> = ({ config, onContinue, onBack }) => {
  const { document, apiBaseUrl, setDocument } = useUserContext();
  const { getToken } = useKeycloak();
  const [formData, setFormData] = useState<Record<string, string>>(() => {
    return document?.customData || {};
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (key: string, value: string) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!document?.lookup) return;
    
    // Validate required fields
    let hasError = false;
    for (const field of config) {
      if (field.required && !formData[field.key]) {
        setError(`Please fill out all required fields: ${field.label}`);
        hasError = true;
        break;
      }
    }
    if (hasError) return;

    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error("Not authenticated");

      const res = await fetch(`${apiBaseUrl}/api/frozendeed/document/${document.lookup}/custom-data`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          customData: formData
        })
      });
      
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to save custom fields');
      }
      
      setDocument({
        ...document,
        customData: formData
      });
      
      setSaving(false);
      onContinue();
    } catch (err: any) {
      setError(err.message || "Failed to save document details");
      setSaving(false);
    }
  };

  if (!config || config.length === 0) {
    useEffect(() => {
      onContinue();
    }, []);
    return null;
  }

  return (
    <form className="w-full flex flex-col gap-6" onSubmit={handleSubmit} autoComplete="off">
      {config.map((field: any) => (
        <div key={field.key} className="flex flex-col gap-2 w-full">
          <label className="text-[#485D7D] text-[14px] font-semibold font-plus-jakarta" htmlFor={`field-${field.key}`}>
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </label>
          {field.description && (
            <p className="text-sm text-[#7C8B9D] mb-1">{field.description}</p>
          )}
          {field.type === 'textarea' ? (
            <textarea
              id={`field-${field.key}`}
              className="w-full px-3 py-2 rounded border border-[#D7DFEC] bg-white text-[#141435] text-[15px] font-plus-jakarta focus:outline-none focus:border-[#0B4BB6]"
              rows={4}
              value={formData[field.key] || ''}
              onChange={e => handleInputChange(field.key, e.target.value)}
              required={field.required}
            />
          ) : (
            <input
              id={`field-${field.key}`}
              type={field.type || 'text'}
              className="w-full px-3 py-2 rounded border border-[#D7DFEC] bg-white text-[#141435] text-[15px] font-plus-jakarta focus:outline-none focus:border-[#0B4BB6]"
              value={formData[field.key] || ''}
              onChange={e => handleInputChange(field.key, e.target.value)}
              required={field.required}
            />
          )}
        </div>
      ))}
      
      {error && <div className="text-red-500 text-sm">{error}</div>}

      <div className="flex gap-4 w-full mt-4">
        {onBack && (
          <button
            type="button"
            className="flex-1 bg-white text-[#0B4BB6] font-bold text-[15px] font-plus-jakarta py-2.5 rounded-full transition-colors border-2 border-[#0B4BB6] hover:bg-gray-50"
            onClick={onBack}
            disabled={saving}
          >
            Back
          </button>
        )}
        <button
          type="submit"
          className={`flex-1 bg-[#0B4BB6] text-white font-bold text-[15px] font-plus-jakarta py-2.5 rounded-full transition-colors border-2 border-[#0B4BB6] ${saving ? 'opacity-60 cursor-not-allowed' : 'hover:bg-[#23235a]'}`}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save & Continue'}
        </button>
      </div>
    </form>
  );
};

export default CustomFieldsCard;
